import * as THREE from 'three';
import { buildBVH } from '../utils/bvh';

// guestMarks — where artists can stand about in a MESH room, found on its own
// floor rather than described (2026-11-01; drawn by components/RoomGuests, which
// has the why). Pure three.js, so it can be tested against the real room models
// in Node: a grid of points, each kept only if a ray down lands on the GROUND
// floor (not a tabletop, not the mezzanine) and rays across at shin and chest
// height and one up find nothing within reach (no wall, bar, table or machine),
// away from where you walk in, the way out and the desk; then picked in twos
// facing each other, the way people stand about.
const GRID = 0.3;          // candidate spacing (room units ≈ 0.66 m)
const MAX_CANDIDATES = 1600;
const STEP = 0.35;         // the down-ray starts this far above the floor (FloorClamp's STEP)
const FLOOR_TOL = 0.06;    // a hit this close to the ground floor's height is ground floor
const CLEAR = 0.3;         // nothing within this of where they stand, at shin and chest
const HEADROOM = 1.1;      // and nothing overhead below this (a table, a shelf)
const MAX_MARKS = 8;
const PAIR = [0.55, 0.95]; // a partner stands this far from the first of a pair
const APART = 0.9;         // the next pair keeps this far from everyone already placed
const KEEP_OFF = { spawn: 0.8, exit: 0.7, desk: 0.95, stage: 0.85, booth: 0.9 };
const BEHIND_BOOTH = 0.25;   // nobody stands behind the decks: past this, on the far side of a booth's facing

function rngFor(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
  let a = (h >>> 0) || 1;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Points to keep away from, in room space: [x, z, radius]. */
function keepOff(room) {
  const out = [];
  if (room?.spawn?.pos) out.push([room.spawn.pos[0], room.spawn.pos[2], KEEP_OFF.spawn]);
  if (room?.exit?.pos) out.push([room.exit.pos[0], room.exit.pos[2], KEEP_OFF.exit + Math.max(room.exit.size?.[0] || 0, room.exit.size?.[2] || 0) / 2]);
  if (room?.frontDesk?.pos) out.push([room.frontDesk.pos[0], room.frontDesk.pos[2], KEEP_OFF.desk]);
  for (const who of Object.values(room?.stage || {})) {
    for (const m of Object.values(who || {})) if (m?.pos) out.push([m.pos[0], m.pos[2], KEEP_OFF.stage]);
  }
  if (room?.booth?.pos) out.push([room.booth.pos[0], room.booth.pos[2], KEEP_OFF.booth]);
  return out;
}

/**
 * Past the way out (seen from where you walk in): the Warehouse's back room is
 * behind its exit, and a visitor heading there leaves before they see anyone.
 */
function beyondExit(room, x, z) {
  const sp = room?.spawn?.pos; const ex = room?.exit?.pos;
  if (!sp || !ex) return false;
  const dx = ex[0] - sp[0]; const dz = ex[2] - sp[2]; const len = Math.hypot(dx, dz);
  if (len < 1e-3) return false;
  return ((x - sp[0]) * dx + (z - sp[2]) * dz) / len > len - 0.1;
}

/** Behind a DJ booth (the side it faces away from): where the performer works, not the crowd. */
function behindBooth(room, x, z) {
  const b = room?.booth;
  if (!b?.pos) return false;
  const r = b.rotY || 0;
  return (x - b.pos[0]) * Math.sin(r) + (z - b.pos[2]) * Math.cos(r) < -BEHIND_BOOTH;
}

/**
 * The ground floor's height: the lowest surface most of the downward rays land
 * on. Not assumed to be 0 — a built room's slab sits under its floor, and the
 * fit puts the BOX's bottom at 0, so the floor itself can be a little above it.
 */
function groundLevel(ys) {
  if (!ys.length) return null;
  const bins = new Map();
  for (const y of ys) { const k = Math.round(y / 0.02); bins.set(k, (bins.get(k) || 0) + 1); }
  const need = Math.max(3, ys.length * 0.15);
  const keys = [...bins.keys()].sort((a, b) => a - b);
  for (const k of keys) {
    const n = (bins.get(k - 1) || 0) + bins.get(k) + (bins.get(k + 1) || 0);
    if (n >= need) return k * 0.02;
  }
  return keys[0] * 0.02;
}

/** Find guest marks on a mesh room's floor. Resolves to [{ pos, rotY }] (may be empty). */
export function findMarks(roomId, room, floor, space) {
  return new Promise((resolve) => {
    space.updateMatrixWorld(true);
    floor.updateMatrixWorld(true);
    try { buildBVH(floor); } catch { /* the plain raycast still works, slower */ }
    // The floor's extent, in room space.
    const wb = new THREE.Box3().setFromObject(floor);
    if (wb.isEmpty()) { resolve([]); return; }
    const lb = new THREE.Box3();
    const c = new THREE.Vector3();
    for (const x of [wb.min.x, wb.max.x]) for (const y of [wb.min.y, wb.max.y]) for (const z of [wb.min.z, wb.max.z]) {
      lb.expandByPoint(space.worldToLocal(c.set(x, y, z)));
    }
    const w = lb.max.x - lb.min.x; const d = lb.max.z - lb.min.z;
    const step = Math.max(GRID, Math.sqrt((w * d) / MAX_CANDIDATES));
    const pts = [];
    for (let x = lb.min.x + step / 2; x < lb.max.x; x += step) for (let z = lb.min.z + step / 2; z < lb.max.z; z += step) pts.push([x, z]);

    const avoid = keepOff(room);
    const top = lb.min.y + STEP + 0.15;   // the down-rays start here: above a floor near the box's bottom, below any tabletop
    const ray = new THREE.Raycaster();
    ray.firstHitOnly = true;
    const o = new THREE.Vector3(); const dir = new THREE.Vector3(); const hitL = new THREE.Vector3();
    const down = new THREE.Vector3(0, -1, 0); const up = new THREE.Vector3(0, 1, 0);
    const across = Array.from({ length: 8 }, (_, i) => new THREE.Vector3(Math.sin((i * Math.PI) / 4), 0, Math.cos((i * Math.PI) / 4)));
    const hits = (from, towards, far) => { ray.set(from, towards); ray.far = far; return ray.intersectObject(floor, true).length > 0; };
    const ok = [];
    const landed = [];   // [x, y, z] where a down-ray found something

    // Pass 1: what is underfoot at each point.
    const probe = ([x, z]) => {
      if (avoid.some(([ax, az, r]) => Math.hypot(x - ax, z - az) < r) || behindBooth(room, x, z) || beyondExit(room, x, z)) return;
      space.localToWorld(o.set(x, top, z));
      ray.set(o, down); ray.far = top - lb.min.y + 0.1;
      const h = ray.intersectObject(floor, true)[0];
      if (h) landed.push([x, space.worldToLocal(hitL.copy(h.point)).y, z]);
    };
    // Pass 2: on the ground floor, with room to stand.
    let level = 0;
    const test = ([x, y, z]) => {
      if (Math.abs(y - level) > FLOOR_TOL) return;
      // Room to stand: nothing across at shin and chest, nothing overhead.
      for (const lift of [0.12, 0.42]) {
        space.localToWorld(o.set(x, y + lift, z));
        for (const a of across) if (hits(o, dir.copy(a).transformDirection(space.matrixWorld), CLEAR)) return;
      }
      space.localToWorld(o.set(x, y + 0.05, z));
      if (hits(o, up, HEADROOM)) return;
      ok.push([x, y, z]);
    };

    // In slices, off the frame: a room is a few thousand rays.
    const run = (list, fn, per, done) => {
      let i = 0;
      const slice = () => {
        const end = Math.min(list.length, i + per);
        for (; i < end; i++) fn(list[i]);
        if (i < list.length) setTimeout(slice, 0); else done();
      };
      slice();
    };
    run(pts, probe, 200, () => {
      level = groundLevel(landed.map((p) => p[1])) ?? 0;
      run(landed, test, 60, () => resolve(pickMarks(roomId, ok, room)));
    });
  });
}

/** Choose up to MAX_MARKS from the free floor: in twos, facing each other. */
export function pickMarks(roomId, ok, room) {
  if (!ok.length) return [];
  const rng = rngFor(`guests:${roomId}`);
  const pool = [...ok];
  for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
  // Where a single faces: the booth if there is one, else where you walk in looking, else the floor's middle.
  const mid = ok.reduce((m, p) => [m[0] + p[0] / ok.length, m[1] + p[2] / ok.length], [0, 0]);
  const focus = room?.booth?.pos ? [room.booth.pos[0], room.booth.pos[2]] : room?.spawn?.look ? [room.spawn.look[0], room.spawn.look[2]] : mid;
  const marks = [];
  const far = (p, r) => marks.every((m) => Math.hypot(m.pos[0] - p[0], m.pos[2] - p[2]) >= r);
  while (marks.length < MAX_MARKS) {
    const a = pool.find((p) => far(p, APART));
    if (!a) break;
    const b = pool.find((p) => p !== a && far(p, 0.5) && (() => { const dd = Math.hypot(p[0] - a[0], p[2] - a[2]); return dd >= PAIR[0] && dd <= PAIR[1]; })());
    if (b && marks.length + 2 <= MAX_MARKS) {
      marks.push({ pos: a, rotY: Math.atan2(b[0] - a[0], b[2] - a[2]) });
      marks.push({ pos: b, rotY: Math.atan2(a[0] - b[0], a[2] - b[2]) });
    } else {
      marks.push({ pos: a, rotY: Math.atan2(focus[0] - a[0], focus[1] - a[2]) });
    }
  }
  return marks;
}
